import { getUID } from '@redviking/argonaut-util/src/getUID';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import type { UiNodeSpec } from './UiNodeSpec';
import type { DeployParamKind } from '@redviking/argonaut-util/src/mes/deployParam.zod';
import { getMetricExpr } from '@redviking/argonaut-util/src/metric-parser/metric-expr';
import { validateSparkplugMetricExpr } from '../../../validations/metric-expr.validations';
import type { UiFlowNodeControl } from '../../controls/UiFlowNodeControl';

export const sparkplugConsumerUiNodeSpec: UiNodeSpec<'sparkplugConsumer'> = {
    requiredSockets: [ 'event' ],
    cfg: () => ({
        disabled: false,
        controlValues: {
            metricExpressions: [],
            configDeploymentParameter: {
                deployParamId: '',
                deployParamKind: 'sparkplugNodeCfg' as DeployParamKind.sparkplugNodeCfg,
                type: 'deployParam',
            },
        },
        id: getUID(),
        kind: 'sparkplugConsumer',
    }),
    controls: getNodeCfg => ({
        configDeploymentParameter: {
            cfg: {
                kind: 'deploymentParameter',
                label: 'Sparkplug Deployment Parameter',
            },
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.configDeploymentParameter,
        },
        metricExpressions: {
            cfg: {
                kind: 'button',
                label: 'Add Metric',
                onClick: () => {
                    const nodeCfg = getNodeCfg();
                    createNodeSocket({
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                metricExpressions: [
                                    ...nodeCfg.controlValues.metricExpressions,
                                    '',
                                ],
                            },
                        },
                    });
                },
            },
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.metricExpressions,
        },
    }),
    inputs: () => ({}),
    kindLabel: 'Sparkplug Consumer',
    nodeCategory: 'external',
    outputs: nodeCfg => {
        const outputs: ReturnType<UiNodeSpec<'sparkplugConsumer'>['outputs']> = {
            event: { socket: 'event', label: 'Updated' },
        };
        for (let i = 0; i < nodeCfg.controlValues.metricExpressions.length; i++) {
            const control: UiFlowNodeControl.Opts<string> = {
                nodeCfg,
                socketDefault: false,
                value: nodeCfg.controlValues.metricExpressions[i],
                cfg: {
                    kind: 'deletableText',
                    placeholder: 'Metric or Metric Expression',
                    getValue: nodeCfg => {
                        if (nodeCfg.kind !== 'sparkplugConsumer') {
                            return null;
                        }
                        return nodeCfg.controlValues.metricExpressions[i];
                    },
                    onChange: newValue => {
                        const newMetricExpressions = [ ...nodeCfg.controlValues.metricExpressions ];
                        newMetricExpressions[i] = newValue;

                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    metricExpressions: newMetricExpressions,
                                },
                            },
                        });
                    },
                    onDelete () {
                        removeNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    metricExpressions: [
                                        ...nodeCfg.controlValues.metricExpressions.slice(0, i),
                                        ...nodeCfg.controlValues.metricExpressions.slice(i + 1),
                                    ],
                                },
                            },
                            direction: 'output',
                            removalMethod: {
                                index: i,
                                prefix: 'output',
                                kind: 'indexedIdentifier',
                            },
                        });
                    },
                },
            };

            outputs[`output${i}`] = { control, socket: 'varValue' };
        }
        return outputs;
    },
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.configDeploymentParameter.deployParamId) {
            errors.configDeploymentParameter = 'Sparkplug Deployment Parameter is required';
        }
        if (!node.controlValues.metricExpressions.length) {
            errors.metricExpressions = 'At least one metric expression is required';
        }
        for (let i = 0; i < node.controlValues.metricExpressions.length; i++) {
            const metricExpr = node.controlValues.metricExpressions[i];
            if (!metricExpr) {
                errors[`output${i}`] = 'Metric expression is required';
            } else {
                try {
                    const parsedMetricExpr = getMetricExpr(metricExpr);
                    const validationResult = validateSparkplugMetricExpr(parsedMetricExpr);
                    if (validationResult === false) {
                        errors[`output${i}`] = 'Metric expression is invalid';
                    } else if (validationResult !== true && validationResult !== '') {
                        errors[`output${i}`] = validationResult;
                    }
                } catch {
                    errors[`output${i}`] = 'Metric expression is invalid';
                }
            }
        }
        return Object.keys(errors).length ? errors : true;
    },
};
